import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { Plus, Edit2, Trash2, Save, X, FolderPlus, Tag, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { seedDefaultCategories } from '../../utils/unschoolingSeedData';

const ICON_OPTIONS = ['⚛️', '🎨', '💻', '🌍', '❤️', '💡', '🎵', '📚', '🔬', '🎭', '⚽', '🍳', '📸', '🎮', '🌱', '🚀', '🎪', '🏛️', '🧪', '🎬'];

const UnschoolingCategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [categoryForm, setCategoryForm] = useState({
    id: '',
    name: '',
    emoji: '⚛️',
    color: 'from-blue-500 to-cyan-500',
    description: '',
    isActive: true,
    order: 0
  });

  const [topicForm, setTopicForm] = useState({
    name: '',
    description: '',
    isActive: true,
    order: 0
  });

  const COLOR_OPTIONS = [
    { value: 'from-blue-500 to-cyan-500', label: 'Blue to Cyan', preview: 'bg-gradient-to-r from-blue-500 to-cyan-500' },
    { value: 'from-pink-500 to-rose-500', label: 'Pink to Rose', preview: 'bg-gradient-to-r from-pink-500 to-rose-500' },
    { value: 'from-purple-500 to-indigo-500', label: 'Purple to Indigo', preview: 'bg-gradient-to-r from-purple-500 to-indigo-500' },
    { value: 'from-orange-500 to-amber-500', label: 'Orange to Amber', preview: 'bg-gradient-to-r from-orange-500 to-amber-500' },
    { value: 'from-green-500 to-emerald-500', label: 'Green to Emerald', preview: 'bg-gradient-to-r from-green-500 to-emerald-500' },
    { value: 'from-yellow-500 to-orange-500', label: 'Yellow to Orange', preview: 'bg-gradient-to-r from-yellow-500 to-orange-500' },
    { value: 'from-red-500 to-pink-500', label: 'Red to Pink', preview: 'bg-gradient-to-r from-red-500 to-pink-500' },
    { value: 'from-teal-500 to-cyan-500', label: 'Teal to Cyan', preview: 'bg-gradient-to-r from-teal-500 to-cyan-500' }
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesRef = collection(db, 'unschooling_categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const categoriesSnap = await getDocs(q);
      
      const categoriesList = [];
      categoriesSnap.forEach(doc => {
        categoriesList.push({ docId: doc.id, ...doc.data() });
      });
      
      setCategories(categoriesList);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    try {
      const categoryData = {
        ...categoryForm,
        updatedAt: new Date().toISOString()
      };

      if (editingCategory) {
        const categoryRef = doc(db, 'unschooling_categories', editingCategory.docId);
        await updateDoc(categoryRef, categoryData);
        toast.success('Category updated successfully');
      } else {
        categoryData.createdAt = new Date().toISOString();
        categoryData.topics = [];
        await addDoc(collection(db, 'unschooling_categories'), categoryData);
        toast.success('Category created successfully');
      }

      setShowCategoryModal(false);
      setEditingCategory(null);
      resetCategoryForm();
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleTopicSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const category = categories.find(c => c.docId === selectedCategory.docId);
      const topics = category.topics || [];

      if (editingTopic !== null) {
        // Update existing topic
        topics[editingTopic] = topicForm;
        toast.success('Topic updated successfully');
      } else {
        // Add new topic
        topics.push(topicForm);
        toast.success('Topic added successfully');
      }

      const categoryRef = doc(db, 'unschooling_categories', selectedCategory.docId);
      await updateDoc(categoryRef, {
        topics: topics,
        updatedAt: new Date().toISOString()
      });

      setShowTopicModal(false);
      setEditingTopic(null);
      setSelectedCategory(null);
      resetTopicForm();
      loadCategories();
    } catch (error) {
      console.error('Error saving topic:', error);
      toast.error('Failed to save topic');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? This will delete the category and all its topics. Content will remain but may not be accessible.')) return;

    try {
      await deleteDoc(doc(db, 'unschooling_categories', categoryId));
      toast.success('Category deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleDeleteTopic = async (category, topicIndex) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;

    try {
      const topics = [...category.topics];
      topics.splice(topicIndex, 1);

      const categoryRef = doc(db, 'unschooling_categories', category.docId);
      await updateDoc(categoryRef, {
        topics: topics,
        updatedAt: new Date().toISOString()
      });

      toast.success('Topic deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Failed to delete topic');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      id: '',
      name: '',
      emoji: '⚛️',
      color: 'from-blue-500 to-cyan-500',
      description: '',
      isActive: true,
      order: categories.length
    });
  };

  const resetTopicForm = () => {
    setTopicForm({
      name: '',
      description: '',
      isActive: true,
      order: 0
    });
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      id: category.id,
      name: category.name,
      emoji: category.emoji,
      color: category.color,
      description: category.description || '',
      isActive: category.isActive !== false,
      order: category.order || 0
    });
    setShowCategoryModal(true);
  };

  const openAddTopic = (category) => {
    setSelectedCategory(category);
    setEditingTopic(null);
    resetTopicForm();
    setTopicForm(prev => ({ ...prev, order: (category.topics || []).length }));
    setShowTopicModal(true);
  };

  const openEditTopic = (category, topicIndex) => {
    setSelectedCategory(category);
    setEditingTopic(topicIndex);
    setTopicForm(category.topics[topicIndex]);
    setShowTopicModal(true);
  };

  const handleSeedCategories = async () => {
    if (!window.confirm('This will add 6 default categories with topics. Continue?')) return;

    try {
      const result = await seedDefaultCategories();
      if (result.success) {
        toast.success(result.message);
        loadCategories();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error seeding categories:', error);
      toast.error('Failed to seed categories');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Categories & Topics
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {categories.length} categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          {categories.length === 0 && (
            <button
              onClick={handleSeedCategories}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={18} />
              Load Default Categories
            </button>
          )}
          <button
            onClick={() => {
              setEditingCategory(null);
              resetCategoryForm();
              setShowCategoryModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus size={18} />
            Add Category
          </button>
        </div>
      </div>

      {/* Categories List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <FolderPlus className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Categories Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Get started by loading default categories or create your own
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleSeedCategories}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Load Default Categories
            </button>
            <button
              onClick={() => {
                setEditingCategory(null);
                resetCategoryForm();
                setShowCategoryModal(true);
              }}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create Custom Category
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((category) => (
            <div
              key={category.docId}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Category Header */}
              <div className={`bg-gradient-to-r ${category.color} p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{category.emoji}</span>
                    <div>
                      <h4 className="text-lg font-semibold text-white">
                        {category.name}
                      </h4>
                      {category.description && (
                        <p className="text-sm text-white/80">{category.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openAddTopic(category)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                      title="Add Topic"
                    >
                      <Plus size={18} />
                    </button>
                    <button
                      onClick={() => openEditCategory(category)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                      title="Edit Category"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.docId)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                      title="Delete Category"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Topics List */}
              <div className="p-4">
                {(!category.topics || category.topics.length === 0) ? (
                  <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                    <Tag className="mx-auto mb-2" size={32} />
                    <p className="text-sm">No topics yet. Add your first topic!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.topics.map((topic, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {topic.name}
                          </div>
                          {topic.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {topic.description}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => openEditTopic(category, index)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                            title="Edit Topic"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteTopic(category, index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Delete Topic"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  resetCategoryForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCategorySubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category ID * (lowercase, no spaces)
                </label>
                <input
                  type="text"
                  value={categoryForm.id}
                  onChange={(e) => setCategoryForm({ ...categoryForm, id: e.target.value.toLowerCase().replace(/\s/g, '-') })}
                  placeholder="e.g., science, arts, technology"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="e.g., Sciences, Arts & Design"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Emoji Icon *
                </label>
                <div className="grid grid-cols-10 gap-2 mb-2">
                  {ICON_OPTIONS.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setCategoryForm({ ...categoryForm, emoji: icon })}
                      className={`text-2xl p-2 rounded-lg border-2 transition-colors ${
                        categoryForm.emoji === icon
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={categoryForm.emoji}
                  onChange={(e) => setCategoryForm({ ...categoryForm, emoji: e.target.value })}
                  placeholder="Or enter custom emoji"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color Gradient *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {COLOR_OPTIONS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setCategoryForm({ ...categoryForm, color: color.value })}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                        categoryForm.color === color.value
                          ? 'border-purple-500'
                          : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-lg ${color.preview}`}></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{color.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Brief description of this category"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={categoryForm.order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryForm.isActive}
                      onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Save size={18} />
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    resetCategoryForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Topic Modal */}
      {showTopicModal && selectedCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingTopic !== null ? 'Edit Topic' : 'Add New Topic'} - {selectedCategory.name}
              </h3>
              <button
                onClick={() => {
                  setShowTopicModal(false);
                  setEditingTopic(null);
                  setSelectedCategory(null);
                  resetTopicForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleTopicSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Topic Name *
                </label>
                <input
                  type="text"
                  value={topicForm.name}
                  onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })}
                  placeholder="e.g., Physics, Music Theory, Programming"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={topicForm.description}
                  onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
                  placeholder="Brief description of this topic"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={topicForm.order}
                    onChange={(e) => setTopicForm({ ...topicForm, order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={topicForm.isActive}
                      onChange={(e) => setTopicForm({ ...topicForm, isActive: e.target.checked })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Save size={18} />
                  {editingTopic !== null ? 'Update Topic' : 'Add Topic'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTopicModal(false);
                    setEditingTopic(null);
                    setSelectedCategory(null);
                    resetTopicForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnschoolingCategoryManager;
